import { type ReactNode, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock3, FileText, Package2, Truck, XCircle } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl } from "@/lib/supabase/client";

const GOLD = "#c5a059";

type ShippingStatus = "payment_pending" | "preparing" | "shipped" | "delivered" | "cancelled";

type MyOrder = {
  id: string;
  status: "pending" | "paid" | "completed" | "cancelled" | string;
  totalTry: number;
  createdAt: number;
  paidAt: number | null;
  deliveredAt: number | null;
  packageKey: string;
  isDigital?: boolean;
  shippingStatus: ShippingStatus | null;
  shippingTrackingNumber: string | null;
  shippingCarrier: string | null;
  shippingUpdatedAt: number | null;
  shippingNote: string | null;
  customer?: {
    phone: string | null;
    addressLine1: string | null;
    city: string | null;
    district: string | null;
    postcode: string | null;
    billingAddress?: {
      addressLine1: string;
      city: string;
      district?: string;
      postcode?: string;
    } | null;
    shippingAddress?: {
      addressLine1: string;
      city: string;
      district?: string;
      postcode?: string;
    } | null;
  };
};

type MyRequest = {
  id: string;
  orderId: string;
  packageKey: string;
  reportLang: string;
  status: "created" | "processing" | "sent" | "failed" | string;
  createdAt: number;
  sentAt: number | null;
  mailStatus: string | null;
  downloadUrl: string;
  adSoyad?: string;
  dogumTarihi?: string;
};

type PanelMeResponse =
  | {
      ok: true;
      orders: MyOrder[];
      requests: MyRequest[];
    }
  | { ok: false; error: string };

function readAccessTokenFromStorage() {
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
    if (!storageKey) return "";
    const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
    const parsed = raw ? (JSON.parse(raw) as any) : null;
    return typeof parsed?.access_token === "string" ? parsed.access_token : "";
  } catch {
    return "";
  }
}

async function fetchPanelMe(token: string): Promise<{ orders: MyOrder[]; requests: MyRequest[] }> {
  const res = await fetch("/api/numeroloji/panel/me", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    const msg = typeof json?.error === "string" ? json.error : "Siparişler alınamadı.";
    throw new Error(msg);
  }

  const data = json as Extract<PanelMeResponse, { ok: true }>;
  return { orders: data.orders ?? [], requests: data.requests ?? [] };
}


async function restoreMobilePurchases(input: { token: string; platform?: "ios" | "android" }) {
  const res = await fetch("/api/numeroloji/payments/mobile/restore", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${input.token}`,
    },
    body: JSON.stringify(input.platform ? { platform: input.platform } : {}),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    const msg = typeof json?.error === "string" ? json.error : "Mobil satın alımlar geri yüklenemedi.";
    throw new Error(msg);
  }

  return {
    items: Array.isArray(json?.items) ? json.items : [],
    summary: Array.isArray(json?.summary) ? json.summary : [],
  };
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function formatDate(ts: number) {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(ts * 1000));
  } catch {
    return String(ts);
  }
}

function formatShortDate(ts: number | null) {
  if (!ts) return "—";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(ts * 1000));
  } catch {
    return String(ts);
  }
}

function shippingStatusLabel(status: ShippingStatus | null, deliveredAt: number | null, fallbackOrderStatus: string) {
  if (status === "delivered" || deliveredAt) return "Teslim Edildi";
  if (status === "shipped") return "Kargoya Verildi";
  if (status === "preparing") return "Hazırlanıyor";
  if (status === "payment_pending") return "Ödeme Bekliyor";
  if (status === "cancelled" || fallbackOrderStatus === "cancelled") return "İptal";
  if (fallbackOrderStatus === "completed") return "Tamamlandı";
  if (fallbackOrderStatus === "paid") return "Hazırlanıyor";
  return "Ödeme Bekliyor";
}

function buildPdfUrl(url: string) {
  if (!url) return "";
  return url.replace(/report\.html(?=\?|$)/i, "report.pdf");
}

function downloadPdfFromReportPdf(opts: { url: string; filename: string }) {
  const pdfUrl = buildPdfUrl(opts.url);
  if (!pdfUrl) throw new Error("PDF indirilemedi.");

  const a = document.createElement("a");
  a.href = pdfUrl;
  a.download = opts.filename;
  a.target = "_blank";
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function PackageBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-[#eadfd0] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700 shadow-sm">
      <span className="h-1.5 w-1.5 rounded-full bg-[#c5a059]" />
      {children}
    </span>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[140px] rounded-[22px] border border-[#eadfd0] bg-white/95 px-5 py-4 shadow-[0_12px_34px_rgba(197,160,89,0.08)]">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-[#8c5b26]">{value}</div>
    </div>
  );
}

function StatusBadge({ label }: { label: string }) {
  const tone =
    label === "Teslim Edildi" || label === "Tamamlandı"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : label === "İptal"
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : label === "Kargoya Verildi"
          ? "border-violet-200 bg-violet-100 text-violet-700"
          : "border-[#eadfd0] bg-[#f7efe4] text-[#8c5b26]";

  const Icon =
    label === "Teslim Edildi" || label === "Tamamlandı"
      ? CheckCircle2
      : label === "İptal"
        ? XCircle
        : label === "Kargoya Verildi"
          ? Truck
          : Clock3;

  return (
    <span className={cn("inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em]", tone)}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function formatPackageLabel(packageKey: string, isDigital?: boolean) {
  if (isDigital) return "Dijital İçerik";
  const key = String(packageKey ?? "").trim().toLowerCase();
  if (!key) return "Normal";
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function UserPanelSiparislerim() {
  const { user } = useSupabaseSession();
  const token = useMemo(() => readAccessTokenFromStorage(), [user?.id]);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreInfo, setRestoreInfo] = useState("");
  const [actionError, setActionError] = useState("");

  const panelQuery = useQuery({
    queryKey: ["panel-my-summary", user?.id],
    queryFn: () => fetchPanelMe(token),
    enabled: Boolean(user?.id && token),
    staleTime: 60_000,
  });

  const orders = panelQuery.data?.orders ?? [];
  const requests = panelQuery.data?.requests ?? [];

  const requestsByOrderId = useMemo(() => {
    const map = new Map<string, MyRequest[]>();
    for (const r of requests) {
      const key = String(r.orderId ?? "");
      if (!key) continue;
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return map;
  }, [requests]);

  const inTransitCount = useMemo(
    () =>
      orders.filter((order) => {
        const label = shippingStatusLabel(order.shippingStatus, order.deliveredAt, order.status);
        return label !== "Teslim Edildi" && label !== "İptal";
      }).length,
    [orders],
  );

  const handleRestorePurchases = async () => {
    if (!token || restoreBusy) return;
    setRestoreBusy(true);
    setActionError("");
    setRestoreInfo("");

    try {
      const restored = await restoreMobilePurchases({ token });
      const count = restored.items.length;
      setRestoreInfo(count > 0 ? `${count} mobil satın alma kaydı geri yüklendi.` : "Aktif mobil satın alma kaydı bulunamadı.");
      panelQuery.refetch();
      globalThis.dispatchEvent?.(new CustomEvent("numeroloji:mobile-payment-restore", { detail: restored }));
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Mobil satın alımlar geri yüklenemedi.");
    } finally {
      setRestoreBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Siparişlerim</div>
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-300">
            Verdiğiniz siparişleri ve analiz durumunu buradan takip edebilirsiniz.
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <SummaryStat label="Toplam Sipariş" value={orders.length} />
          <SummaryStat label="Yoldakiler" value={inTransitCount} />
          <button
            type="button"
            onClick={handleRestorePurchases}
            disabled={!token || restoreBusy}
            className="h-11 rounded-2xl border border-[#eadfd0] bg-white px-4 text-xs font-semibold uppercase tracking-[0.14em] text-[#8c5b26] transition hover:bg-[#faf5ef] disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-slate-900/40 dark:text-slate-100 dark:hover:bg-slate-900/70"
          >
            {restoreBusy ? "Geri yükleniyor..." : "Satın alımları geri yükle"}
          </button>
        </div>
      </div>

      {restoreInfo ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-200">{restoreInfo}</div> : null}

      <Card className="overflow-hidden rounded-[30px] border border-[#eadfd0] bg-[linear-gradient(180deg,#fffaf6_0%,#fffdfb_100%)] p-5 shadow-[0_24px_80px_rgba(197,160,89,0.08)] dark:border-white/10 dark:bg-white/5">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="text-sm font-bold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-300">Siparişler</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{orders.length ? `Toplam: ${orders.length}` : ""}</div>
        </div>

        {actionError ? <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-200">{actionError}</div> : null}

        {panelQuery.isLoading ? (
          <div className="rounded-[24px] border border-dashed border-[#eadfd0] bg-white/70 px-6 py-10 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Yükleniyor...
          </div>
        ) : panelQuery.isError ? (
          <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-6 py-10 text-sm text-rose-700 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-200">
            {(panelQuery.error as Error).message}
          </div>
        ) : !orders.length ? (
          <div className="rounded-[24px] border border-dashed border-[#eadfd0] bg-white/70 px-6 py-10 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Henüz bir siparişiniz yok.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[26px] border border-[#efe6db] bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] dark:border-white/10 dark:bg-[#120d14]/70">
            <table className="min-w-[980px] w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-[#f1e8dd] dark:border-white/10">
                  <th className="px-6 py-5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">Sipariş No</th>
                  <th className="px-6 py-5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">Paket Tipi</th>
                  <th className="px-6 py-5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">Tutar</th>
                  <th className="px-6 py-5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">Durum</th>
                  <th className="px-6 py-5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">Sipariş Tarihi</th>
                  <th className="px-6 py-5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const related = requestsByOrderId.get(order.id) ?? [];
                  const sentRequest = related.find((request) => request.status === "sent") ?? null;
                  const statusLabel = shippingStatusLabel(order.shippingStatus, order.deliveredAt, order.status);

                  const subDateLabel = order.deliveredAt
                    ? `Teslim: ${formatShortDate(order.deliveredAt)}`
                    : order.shippingUpdatedAt
                      ? `Son durum: ${formatShortDate(order.shippingUpdatedAt)}`
                      : order.paidAt
                        ? `Ödeme: ${formatShortDate(order.paidAt)}`
                        : "Takipte";

                  return (
                    <tr key={order.id} className="border-b border-[#f4ebdf] last:border-b-0 dark:border-white/10">
                      <td className="px-6 py-6 align-middle">
                        <div className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">{order.id}</div>
                      </td>

                      <td className="px-6 py-6 align-middle">
                        <PackageBadge>{formatPackageLabel(order.packageKey, order.isDigital)}</PackageBadge>
                      </td>

                      <td className="px-6 py-6 align-middle text-base font-bold" style={{ color: GOLD }}>
                        {formatTry(order.totalTry)}
                      </td>

                      <td className="px-6 py-6 align-middle">
                        <StatusBadge label={statusLabel} />
                        {order.shippingCarrier || order.shippingTrackingNumber ? (
                          <div className="mt-2 text-xs text-slate-500 dark:text-slate-300">
                            {[order.shippingCarrier, order.shippingTrackingNumber].filter(Boolean).join(" • ")}
                          </div>
                        ) : null}
                      </td>

                      <td className="px-6 py-6 align-middle">
                        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{formatShortDate(order.createdAt)}</div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{subDateLabel}</div>
                      </td>

                      <td className="px-6 py-6 align-middle">
                        <div className="flex flex-wrap items-center gap-3">
                          {sentRequest?.downloadUrl ? (
                            <button
                              type="button"
                              disabled={pdfBusyId === order.id}
                              onClick={async () => {
                                try {
                                  setActionError("");
                                  setPdfBusyId(order.id);
                                  downloadPdfFromReportPdf({
                                    url: sentRequest.downloadUrl,
                                    filename: `${order.id}.pdf`,
                                  });
                                } catch (error) {
                                  setActionError(error instanceof Error ? error.message : "PDF indirilemedi.");
                                } finally {
                                  setPdfBusyId(null);
                                }
                              }}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
                            >
                              <FileText className="h-4 w-4" />
                              {pdfBusyId === order.id ? "Hazırlanıyor..." : "Detaylar"}
                            </button>
                          ) : null}

                          {!sentRequest?.downloadUrl ? (
                            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                              <Package2 className="h-4 w-4" />
                              İşlem yok
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
